/**
 * User Select Component
 *
 * Searchable dropdown for selecting users in dashboard filters
 */

import { Input } from '@/shared/ui/input';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { User } from '../api/filtersApi';

interface UserSelectProps {
  users: User[];
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
}

export default function UserSelect({ users, value, onChange, isLoading }: UserSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;

    const searchTerm = search.toLowerCase().trim();
    const searchWords = searchTerm.split(/\s+/);

    return users.filter((user) => {
      const fullName = user.full_name.toLowerCase();
      // All search words must be found in the full name
      return searchWords.every((word) => fullName.includes(word));
    });
  }, [users, search]);

  const selectedUser = useMemo(() => users.find((u) => String(u.id) === value), [users, value]);

  const handleSelect = (userId: string) => {
    onChange(userId);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative">
      {/* Display selected user or search input */}
      <div
        className="relative cursor-pointer"
        onClick={() => {
          if (value !== 'all') {
            // If a user is selected, clicking should open dropdown
            setIsOpen(!isOpen);
          }
        }}
      >
        {value === 'all' || !selectedUser ? (
          <div
            className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors bg-orange-600 text-white cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            All Assignees
          </div>
        ) : (
          <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-colors bg-orange-600 text-white">
            <span>{selectedUser.full_name}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSelect('all');
              }}
              className="ml-2 hover:bg-orange-700 rounded p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />

          {/* Dropdown content */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-[61] max-h-64 flex flex-col">
            {/* Search input */}
            <div className="p-2 border-b border-border sticky top-0 bg-card">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* User list */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  Loading...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  No users found
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleSelect('all')}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                      value === 'all' ? 'bg-orange-600 text-white' : 'text-foreground'
                    }`}
                  >
                    All Assignees
                  </button>
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelect(String(user.id))}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                        value === String(user.id) ? 'bg-orange-600 text-white' : 'text-foreground'
                      }`}
                    >
                      <div>{user.full_name}</div>
                      {user.job_title && (
                        <div className="text-xs text-muted-foreground">{user.job_title}</div>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
